import React from "react";
import { useEffect, useMemo, useRef, useState } from 'react';
import DrawerShell from '../dashboard/components/PreLoanSteps/insurance/DrawerShell';
import { useClienteDetalle } from './hooks';
import { vencimientosApi } from './api';
import { TYPE_META, fmtMoney, fmtDate, statusMeta, mapPrimaStatus, mapEffectiveStatus, parseDate } from './vencimientosConfig';

function StatusBadge({ estado, needsReview }) {
  const meta = statusMeta(estado, needsReview);
  return (
    <span className={`venc-badge tone-${meta.tone}`}>
      <i className={`pi ${meta.icon}`} />{meta.label}
    </span>
  );
}

/* Tira "Proximos vencimientos": resumen scrollable del carro activo
   (primas no pagadas + renovacion de placa pendiente), ordenado por fecha. */
function ProximosStrip({ car }) {
  const tiles = [];
  (car.insurance?.primas || []).forEach((p) => {
    if (p.status === 'paid' || p.status === 'waived' || p.status === 'cancelled') return;
    tiles.push({
      key: `S-${p.id}`,
      icon: TYPE_META.seguro.icon,
      label: TYPE_META.seguro.label,
      estado: mapPrimaStatus(p.status, p.due_date),
      dueDate: p.due_date,
      amount: p.amount,
      subRef: p.period_label || 'Prima',
    });
  });
  if (car.plate && !car.plate.renovada) {
    tiles.push({
      key: `P-${car.plate.id}`,
      icon: TYPE_META.placa.icon,
      label: TYPE_META.placa.label,
      estado: mapEffectiveStatus(car.plate.effective_status),
      dueDate: car.plate.next_renewal_date || car.plate.expires_at,
      amount: null,
      subRef: car.plate.plate_number || 'Renovacion',
    });
  }
  (car.tenencia || []).forEach((t) => {
    if (t.status === 'paid' || t.status === 'waived' || t.status === 'cancelled') return;
    tiles.push({
      key: `T-${t.id}`,
      icon: TYPE_META.tenencia.icon,
      label: TYPE_META.tenencia.label,
      estado: mapPrimaStatus(t.status, t.due_date),
      dueDate: t.due_date,
      amount: t.amount,
      subRef: t.period_label || 'Tenencia',
    });
  });
  // Renta: solo la próxima cuota viva (no inundar el strip con 48-60 meses).
  const rentaViva = (car.renta || [])
    .filter((r) => r.status !== 'paid' && r.status !== 'waived' && r.status !== 'cancelled')
    .sort((a, b) => (parseDate(a.due_date) || 0) - (parseDate(b.due_date) || 0))[0];
  if (rentaViva) {
    tiles.push({
      key: `R-${rentaViva.id}`,
      icon: TYPE_META.renta.icon,
      label: TYPE_META.renta.label,
      estado: mapPrimaStatus(rentaViva.status, rentaViva.due_date),
      dueDate: rentaViva.due_date,
      amount: rentaViva.amount,
      subRef: rentaViva.period_label || 'Renta',
    });
  }
  tiles.sort((a, b) => (parseDate(a.dueDate) || 0) - (parseDate(b.dueDate) || 0));
  if (!tiles.length) return null;

  return (
    <div>
      <div className="section-label">Proximos vencimientos<span className="line" /></div>
      <div className="c360-tl">
        {tiles.map((t) => {
          const cls = t.estado === 'vencido' ? 'is-overdue' : t.estado === 'por_vencer' ? 'is-due' : '';
          return (
            <div className={`tile ${cls}`} key={t.key}>
              <div className="head"><i className={`pi ${t.icon}`} /><span>{t.label}</span></div>
              <div className="d">{parseDate(t.dueDate) ? fmtDate(t.dueDate) : 'Revisar'}</div>
              <div className="amt">{fmtMoney(t.amount)}</div>
              <div className="lbl">{t.subRef}</div>
              <StatusBadge estado={t.estado} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Seccion Seguros: primas + Marcar pagado + Completar primas faltantes + editar prima. */
function SegurosSection({ insurance, termYears, busyId, busyComplete, onPay, onComplete, onEdit }) {
  const inpStyle = { maxWidth: 120, padding: '6px 8px', border: '1.5px solid var(--pei-cyan-bright)', borderRadius: 8, font: 'inherit' };
  const primas = insurance?.primas || [];
  const existentes = primas.length;
  const planSugerido = Math.max(termYears || 0, insurance?.payment_plan_years || 0, existentes, 1);

  const [target, setTarget] = useState(planSugerido);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ due_date: '', amount: '' });

  // Resincroniza el target sugerido al cambiar de poliza (otro carro).
  useEffect(() => {
    setTarget(planSugerido);
    setEditingId(null);
  }, [insurance?.id, termYears, insurance?.payment_plan_years]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!insurance) {
    return (
      <div className="section-block">
        <div className="sh">
          <span className={`tico ${TYPE_META.seguro.toneClass}`}><i className={`pi ${TYPE_META.seguro.icon}`} /></span>
          <h4>Seguros</h4>
        </div>
        <div className="empty-mini">Este carro no tiene poliza capturada.</div>
      </div>
    );
  }

  const total = primas.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const faltan = Math.max(0, target - existentes);

  // Prima "actual": la primera no pagada por fecha de vencimiento (recibe el realce cyan).
  const currentId = (() => {
    const pend = primas.filter((p) => p.status !== 'paid' && p.status !== 'waived' && p.status !== 'cancelled');
    pend.sort((a, b) => (parseDate(a.due_date) || 0) - (parseDate(b.due_date) || 0));
    return pend[0]?.id ?? null;
  })();

  function startEdit(p) {
    setEditingId(p.id);
    setDraft({
      due_date: p.due_date && /^\d{4}-\d{2}-\d{2}/.test(p.due_date) ? p.due_date.slice(0, 10) : '',
      amount: p.amount ?? '',
    });
  }

  async function saveEdit(id) {
    await onEdit(id, {
      due_date: draft.due_date || null,
      amount: draft.amount === '' ? null : Number(draft.amount),
    });
    setEditingId(null);
  }

  return (
    <div className="section-block">
      <div className="sh">
        <span className={`tico ${TYPE_META.seguro.toneClass}`}><i className={`pi ${TYPE_META.seguro.icon}`} /></span>
        <h4>Seguros</h4>
        <span className="meta">{existentes} primas · total {fmtMoney(total)}</span>
      </div>
      <div className="policy-head">
        <div className="pi-info">
          <b>Poliza {insurance.policy_number || '—'}</b>
          <span>
            {[insurance.company_display, insurance.coverage && `Cobertura ${insurance.coverage}`, insurance.broker && `Broker ${insurance.broker}`]
              .filter(Boolean)
              .join(' · ') || '—'}
          </span>
        </div>
        {insurance.effective_status && <StatusBadge estado={mapEffectiveStatus(insurance.effective_status)} />}
      </div>

      {existentes === 0 ? (
        <div className="empty-mini">Sin primas registradas.</div>
      ) : (
        <div className="prima-list">
          {primas.map((p) => {
            const isEditing = editingId === p.id;
            return (
              <div className={`prima ${p.id === currentId ? 'current' : ''}`} key={p.id}>
                <div className="per">
                  <b>{p.period_label || `Prima ${p.id}`}</b>
                  <span>Prima</span>
                </div>
                {isEditing ? (
                  <>
                    <input
                      className="venc-edit-amt"
                      type="number"
                      step="0.01"
                      value={draft.amount}
                      placeholder="Monto"
                      onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
                    />
                    <input
                      className="venc-edit-date"
                      type="date"
                      value={draft.due_date}
                      onChange={(e) => setDraft((d) => ({ ...d, due_date: e.target.value }))}
                    />
                    <div className="pactions">
                      <button type="button" className="venc-btn primary sm" disabled={busyId === p.id} onClick={() => saveEdit(p.id)}>
                        <i className="pi pi-check" />Guardar
                      </button>
                      <button type="button" className="venc-btn ghost sm" onClick={() => setEditingId(null)}>
                        Cancelar
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pamt">{fmtMoney(p.amount)}</div>
                    <div className="pdate">
                      {fmtDate(p.due_date)}
                      {p.paid_at && <span className="paidon">Pagado {fmtDate(p.paid_at)}</span>}
                    </div>
                    <StatusBadge estado={mapPrimaStatus(p.status, p.due_date)} />
                    <div className="pactions">
                      {p.status !== 'paid' ? (
                        <button type="button" className="venc-btn primary sm" disabled={busyId === p.id} onClick={() => onPay(p.id, true)}>
                          <i className="pi pi-check" />Marcar pagado
                        </button>
                      ) : (
                        <button type="button" className="venc-btn ghost sm" disabled={busyId === p.id} onClick={() => onPay(p.id, false)}>
                          Revertir
                        </button>
                      )}
                      <button type="button" className="venc-btn ghost sm" title="Editar prima" onClick={() => startEdit(p)}>
                        <i className="pi pi-pencil" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Completar primas faltantes: el sistema proyecta las que faltan (no captura manual). */}
      <div className="prima-complete" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: 'var(--pei-text-helper)' }}>
          {faltan > 0 ? `Faltan ${faltan} prima(s) para una poliza de` : `${existentes} prima(s) · poliza de`}
        </span>
        <input
          style={{ ...inpStyle, maxWidth: 70 }}
          type="number"
          min={Math.max(existentes, 1)}
          max={20}
          value={target}
          onChange={(e) => setTarget(Math.max(1, Number(e.target.value) || 1))}
        />
        <span style={{ fontSize: 13, color: 'var(--pei-text-helper)' }}>años</span>
        <button
          type="button"
          className="venc-btn primary sm"
          disabled={busyComplete || target <= existentes}
          onClick={() => onComplete(insurance.id, target)}
        >
          <i className="pi pi-bolt" />Completar primas
        </button>
      </div>
    </div>
  );
}

/* Seccion Placas: estado + boton "Marcar renovada" true/false -> renovarPlaca. */
function PlacasSection({ plate, busyPlate, onRenew }) {
  if (!plate) {
    return (
      <div className="section-block">
        <div className="sh">
          <span className={`tico ${TYPE_META.placa.toneClass}`}><i className={`pi ${TYPE_META.placa.icon}`} /></span>
          <h4>Placas</h4>
        </div>
        <div className="empty-mini">Este carro no tiene placas capturadas.</div>
      </div>
    );
  }
  return (
    <div className="section-block">
      <div className="sh">
        <span className={`tico ${TYPE_META.placa.toneClass}`}><i className={`pi ${TYPE_META.placa.icon}`} /></span>
        <h4>Placas</h4>
        <StatusBadge estado={plate.renovada ? 'renovado' : mapEffectiveStatus(plate.effective_status)} />
      </div>
      <div className="kv-grid">
        <div className="kv"><span className="k">Placas</span><span className="v">{plate.plate_number || '—'}</span></div>
        <div className="kv"><span className="k">Entidad</span><span className="v">{plate.plate_state || '—'}</span></div>
        <div className="kv"><span className="k">Vence</span><span className="v">{fmtDate(plate.expires_at)}</span></div>
        <div className="kv"><span className="k">Proxima renovacion</span><span className="v">{fmtDate(plate.next_renewal_date)}</span></div>
      </div>
      <div className="prima-list">
        <div className="prima">
          <div className="per"><b>Renovacion</b><span>{plate.renovada ? 'Renovada' : 'Pendiente'}</span></div>
          <div className="pdate">{plate.renovada ? 'Marcada como renovada' : 'Sin renovar'}</div>
          <div className="pactions">
            {!plate.renovada ? (
              <button
                type="button"
                className="venc-btn primary sm"
                disabled={busyPlate}
                onClick={() => onRenew(plate.id, true)}
              >
                <i className="pi pi-check" />Marcar renovada
              </button>
            ) : (
              <button
                type="button"
                className="venc-btn ghost sm"
                disabled={busyPlate}
                onClick={() => onRenew(plate.id, false)}
              >
                Revertir
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Seccion generica de obligaciones programadas (Tenencia / Renta).
   Cada item: period_label + monto + fecha + badge de estado + Marcar pagado/Revertir.
   Para Renta (48-60 cuotas) la lista va en un contenedor con altura maxima + scroll
   y un resumen "pagadas X de N". Reusa StatusBadge + mapPrimaStatus. */
function ObligacionesSection({ tipo, items, busyId, onPay, scroll, bloqueado }) {
  const meta = TYPE_META[tipo];
  const lista = items || [];
  const total = lista.reduce((s, o) => s + (Number(o.amount) || 0), 0);
  // Condonadas (p.ej. por recuperación de auto) NO son pagadas: se cuentan aparte.
  const pagadas = lista.filter((o) => o.status === 'paid').length;
  const condonadas = lista.filter((o) => o.status === 'waived').length;

  if (!lista.length) {
    return (
      <div className="section-block">
        <div className="sh">
          <span className={`tico ${meta.toneClass}`}><i className={`pi ${meta.icon}`} /></span>
          <h4>{meta.label}</h4>
        </div>
        <div className="empty-mini">Este carro no tiene {meta.label.toLowerCase()} registrada.</div>
      </div>
    );
  }

  return (
    <div className="section-block">
      <div className="sh">
        <span className={`tico ${meta.toneClass}`}><i className={`pi ${meta.icon}`} /></span>
        <h4>{meta.label}</h4>
        <span className="meta">
          pagadas {pagadas}{condonadas ? ` · condonadas ${condonadas}` : ''} de {lista.length} · total {fmtMoney(total)}
        </span>
      </div>
      <div className={`prima-list ${scroll ? 'venc-scroll-list' : ''}`}>
        {lista.map((o) => {
          const isPaid = o.status === 'paid';
          return (
            <div className="prima" key={o.id}>
              <div className="per">
                <b>{o.period_label || `#${o.id}`}</b>
                <span>{meta.label}</span>
              </div>
              <div className="pamt">{fmtMoney(o.amount)}</div>
              <div className="pdate">
                {fmtDate(o.due_date)}
                {o.paid_at && <span className="paidon">Pagado {fmtDate(o.paid_at)}</span>}
              </div>
              <StatusBadge estado={mapPrimaStatus(o.status, o.due_date)} />
              <div className="pactions">
                {/* Condonada o contrato cerrado por recuperación: se cambia desde
                    Cartera (deshacer recuperación), no marcando aquí. */}
                {o.status === 'waived' || bloqueado ? null : !isPaid ? (
                  <button type="button" className="venc-btn primary sm" disabled={busyId === o.id} onClick={() => onPay(o.id, true)}>
                    <i className="pi pi-check" />Marcar pagado
                  </button>
                ) : (
                  <button type="button" className="venc-btn ghost sm" disabled={busyId === o.id} onClick={() => onPay(o.id, false)}>
                    Revertir
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ClienteDrawer({ userId, focusUid, visible, onClose, toast, onMutated }) {
  const { data, loading, refresh } = useClienteDetalle(visible ? userId : null);
  const [activeCar, setActiveCar] = useState(0);
  const [busyPaymentId, setBusyPaymentId] = useState(null);
  const [busyPlate, setBusyPlate] = useState(false);
  const [busyComplete, setBusyComplete] = useState(false);
  const [busyObligacionId, setBusyObligacionId] = useState(null);

  const carros = data?.carros || [];

  // Marca para que el carro inicial se fije UNA sola vez por apertura/cliente.
  // Guarda el userId ya inicializado; al cerrar se resetea a null.
  const initedForRef = useRef(null);

  // Al abrir/cambiar de cliente, enfoca el carro clicado por budget_id (el uid
  // del feed es "<budget_id>-S|P-..."). IMPORTANTE: solo una vez por apertura.
  // Los refresh posteriores (p.ej. tras agregar/editar primas) NO deben
  // reposicionar el carro: el usuario pudo haber cambiado de carro con los chips
  // y agregado primas ahi; reposicionar lo regresaria al carro del focusUid (o al 0).
  useEffect(() => {
    if (!visible) { initedForRef.current = null; return; }
    if (!carros.length) return;
    if (initedForRef.current === userId) return; // ya inicializado para este cliente
    initedForRef.current = userId;
    let idx = 0;
    if (focusUid) {
      const bid = String(focusUid).split('-')[0];
      const found = carros.findIndex((c) => String(c.budget_id) === bid);
      if (found >= 0) idx = found;
    }
    setActiveCar(idx);
  }, [data, focusUid, userId, visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const car = carros[activeCar] || null;
  const user = data?.user || null;

  const carLabel = useMemo(() => (c) => {
    const v = c?.vehicle || {};
    return [v.brand, v.model, v.year].filter(Boolean).join(' ') || `Carro ${c?.budget_id || ''}`;
  }, []);

  async function handlePay(paymentId, paid) {
    setBusyPaymentId(paymentId);
    try {
      await vencimientosApi.marcarPago(paymentId, paid);
      toast?.current?.show?.({
        severity: 'success',
        summary: paid ? 'Pago registrado' : 'Pago revertido',
        life: 2500,
      });
      await refresh();
      onMutated?.(); // tambien refresca el feed del dashboard de atras
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'No se pudo actualizar el pago', life: 3000 });
    } finally {
      setBusyPaymentId(null);
    }
  }

  async function handleRenew(plateId, renovada) {
    setBusyPlate(true);
    try {
      await vencimientosApi.renovarPlaca(plateId, renovada);
      toast?.current?.show?.({
        severity: 'success',
        summary: renovada ? 'Placa marcada como renovada' : 'Renovacion revertida',
        life: 2500,
      });
      await refresh();
      onMutated?.(); // tambien refresca el feed del dashboard de atras
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'No se pudo actualizar la placa', life: 3000 });
    } finally {
      setBusyPlate(false);
    }
  }

  async function handleComplete(insuranceId, years) {
    setBusyComplete(true);
    try {
      const res = await vencimientosApi.completarPrimas(insuranceId, years);
      const n = res?.data?.creadas ?? 0;
      toast?.current?.show?.({
        severity: 'success',
        summary: n > 0 ? `${n} prima(s) agregada(s)` : 'La poliza ya estaba completa',
        life: 2500,
      });
      await refresh();
      onMutated?.(); // tambien refresca el feed del dashboard de atras
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'No se pudieron completar las primas', life: 3000 });
    } finally {
      setBusyComplete(false);
    }
  }

  async function handleEditPrima(paymentId, payload) {
    setBusyPaymentId(paymentId);
    try {
      await vencimientosApi.editarPrima(paymentId, payload);
      toast?.current?.show?.({ severity: 'success', summary: 'Prima actualizada', life: 2500 });
      await refresh();
      onMutated?.(); // tambien refresca el feed del dashboard de atras
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'No se pudo editar la prima', life: 3000 });
    } finally {
      setBusyPaymentId(null);
    }
  }

  async function handleObligacion(id, paid) {
    setBusyObligacionId(id);
    try {
      await vencimientosApi.marcarObligacion(id, paid);
      toast?.current?.show?.({
        severity: 'success',
        summary: paid ? 'Pago registrado' : 'Pago revertido',
        life: 2500,
      });
      await refresh();
      onMutated?.(); // tambien refresca el feed del dashboard de atras
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'No se pudo actualizar el pago', life: 3000 });
    } finally {
      setBusyObligacionId(null);
    }
  }

  return (
    <DrawerShell visible={visible} onClose={onClose}>
      {/* La clase `venc` es necesaria aqui: DrawerShell porta este contenido a
          document.body (fuera del <div.venc> de la pagina), asi que sin ella las
          reglas scopeadas .venc .venc-btn / .venc-badge / .tico no aplicarian. */}
      <div className="venc venc-drawer-body">
        <div className="dh">
          <div className="veh-ic"><i className="pi pi-user" /></div>
          <div className="ttl">
            <h3>
              {user?.name || (loading ? 'Cargando...' : 'Cliente')}
              {user?.id != null && <span className="uid-chip" title="ID de usuario">#{user.id}</span>}
            </h3>
            {user?.contacto && (
              <div className="meta">
                {user.contacto.email && <span><i className="pi pi-envelope" />{user.contacto.email}</span>}
                {user.contacto.phone && <span><i className="pi pi-phone" />{user.contacto.phone}</span>}
              </div>
            )}
          </div>
          <button type="button" className="x" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        {/* Switcher de carros (uno por Budget del user) */}
        {carros.length > 0 && (
          <div className="car-switch">
            {carros.map((c, i) => (
              <button
                key={c.budget_id || i}
                type="button"
                className={`car-chip ${i === activeCar ? 'on' : ''}`}
                onClick={() => setActiveCar(i)}
              >
                {carLabel(c)}
                <span>{c.vehicle?.vin || `Budget ${c.budget_id}`}</span>
              </button>
            ))}
          </div>
        )}

        <div className="dbody">
          {loading && !data ? (
            <div className="venc-loading">Cargando detalle...</div>
          ) : !car ? (
            <div className="empty-mini">Este cliente no tiene carros con vencimientos.</div>
          ) : (
            <>
              {car.contrato_recuperado && (
                <div className="aviso-recuperado">
                  <i className="pi pi-info-circle" />
                  <span>
                    Contrato cerrado por recuperación de auto{car.recuperacion_fecha ? ` el ${car.recuperacion_fecha}` : ''}.
                    El seguro, las placas y la tenencia siguen activos; si se van a cancelar, hazlo desde cada uno.
                  </span>
                </div>
              )}
              {car.vehicle && (
                <div className="kv-grid">
                  <div className="kv"><span className="k">Vehiculo</span><span className="v">{carLabel(car)}</span></div>
                  <div className="kv"><span className="k">VIN</span><span className="v">{car.vehicle.vin || '—'}</span></div>
                </div>
              )}
              <ProximosStrip car={car} />
              <SegurosSection
                insurance={car.insurance}
                termYears={car.term_years}
                busyId={busyPaymentId}
                busyComplete={busyComplete}
                onPay={handlePay}
                onComplete={handleComplete}
                onEdit={handleEditPrima}
              />
              <PlacasSection plate={car.plate} busyPlate={busyPlate} onRenew={handleRenew} />
              <ObligacionesSection
                tipo="tenencia"
                items={car.tenencia}
                busyId={busyObligacionId}
                onPay={handleObligacion}
              />
              <ObligacionesSection
                tipo="renta"
                items={car.renta}
                busyId={busyObligacionId}
                onPay={handleObligacion}
                scroll
                bloqueado={car.contrato_recuperado}
              />
            </>
          )}
        </div>

        <div className="df">
          <span className="hint">
            <i className="pi pi-info-circle" /> {carros.length} carro(s) · {user?.name || ''}
          </span>
          <span className="grow" />
          <button type="button" className="venc-btn ghost sm" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </DrawerShell>
  );
}
